import { useNavigate } from "react-router-dom";
import { useNotifications } from "@/hooks/useNotifications";
import { useLocale } from "@/hooks/useLocale";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Bell, Check, X, UserPlus, Swords, Info, CheckCheck } from "lucide-react";
import { cn } from "@/lib/utils";

const TYPE_ICONS: Record<string, React.ElementType> = {
  friend_request: UserPlus,
  turn_alert: Swords,
  info: Info,
};

function timeAgo(dateStr: string, t: { forum_time_just_now: string; forum_time_minutes_ago: string; forum_time_hours_ago: string; forum_time_days_ago: string }): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return t.forum_time_just_now;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}${t.forum_time_minutes_ago}`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}${t.forum_time_hours_ago}`;
  const days = Math.floor(hours / 24);
  return `${days}${t.forum_time_days_ago}`;
}

export function NotificationBell() {
  const navigate = useNavigate();
  const { t } = useLocale();
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearNotification } = useNotifications();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9" aria-label={t.aria_notifications}>
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground ring-2 ring-background">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80" align="end" forceMount>
        <DropdownMenuLabel className="flex items-center justify-between">
          <span className="text-sm font-medium">{t.aria_notifications}</span>
          {unreadCount > 0 && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                markAllAsRead();
              }}
              className="text-[11px] text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              <CheckCheck className="w-3 h-3" /> {t.notif_mark_all_read}
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {notifications.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            {t.notif_empty}
          </div>
        ) : (
          <div className="max-h-72 overflow-y-auto">
            {notifications.map((n) => {
              const Icon = TYPE_ICONS[n.type] || Info;
              return (
                <button
                  key={n.id}
                  className={cn(
                    "flex items-start gap-3 w-full text-left px-3 py-2.5 hover:bg-accent/50 transition-colors",
                    !n.read_at && "bg-primary/5"
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!n.read_at) markAsRead(n.id);
                    // Only navigate to trusted in-app paths (must start with '/') to
                    // prevent javascript: or data: protocol injection from DB content.
                    if (n.link && n.link.startsWith('/')) navigate(n.link);
                  }}
                >
                  <Icon className={cn("w-4 h-4 mt-0.5 shrink-0", !n.read_at ? "text-primary" : "text-muted-foreground")} />
                  <div className="flex-1 min-w-0">
                    <p className={cn("text-sm", !n.read_at && "font-medium")}>{n.title}</p>
                    {n.body && (
                      <p className="text-xs text-muted-foreground truncate">{n.body}</p>
                    )}
                    <p className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(n.created_at, t)}</p>
                  </div>
                  <span
                    role="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      clearNotification(n.id);
                    }}
                    className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5"
                  >
                    <X className="w-3 h-3" />
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
